import {
  SpaceSkyForegroundScope,
  SpaceSkySurfaceContext,
  useIsSpaceBackgroundEnabled,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { PortalHost } from "@-/com.npmjs.react-native-teleport";
import { cn } from "@-/com.npmjs.cn";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { View } from "@-/com.npmjs.react-native";
import Animated, { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { isSentenceProductionDrillKind } from "@-/dev.improve.haniverse.lib.drill.entities";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { usePersistentKeyboardVisible } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import {
  LearnMapCardsBackdrop,
  LearnMapSheetCovered,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useContext } from "react";

import { LearnFlashcardActionButton } from "#learn-card-action-button";
import { LearnFlashcardLessonControls } from "#learn-card-lesson-controls";
import { LearnFlashcardQuizControls } from "#learn-card-quiz-controls";
import { LearnFlashcardRatingButtons } from "#learn-card-rating-buttons";
import { LearnFlashcardSlideControls } from "#learn-card-slide-controls";

/**
 * The stationary action row in portrait and icon rail on the right in landscape. What it
 * holds follows the face:
 *
 *   * an unrevealed free-text review gets its answer input; a lesson gets its Reveal pill;
 *   * the ANSWER (and its Info view) gets the floating action buttons for the current item;
 *   * committed actions keep their buttons, with progress and retry inside the selected button.
 *
 * A sentence-production drill reveals in place and rates from the strip inside its own card, so it
 * leaves this row empty. The row sits directly above the bottom tabs, inset enough that the tab
 * bar's fade never overlaps the buttons' bottom border. It never moves during reveals or
 * dismissals — the stage above animates and clips around it — and downward Hard/Good exits pass
 * under it, never over. Landscape keeps typed answers below the card while the actions stay
 * in the rail. Typed answers overlay the landscape card's lower inset, so the card keeps its
 * full height; retaining that input host through reveals preserves keyboard focus.
 *
 * When Learn's map opens the row stays put too: its backdrop dissolves into the map with the
 * cards', and the map's sheet slides in over its buttons (`LearnMapSheetCovered`).
 */
export function LearnFlashcardActionArea({
  isActive,
  isCovered = false,
}: {
  isActive: boolean;
  /**
   * Another screen's controls (the Learn summary's) float over this row. It stays mounted, since
   * it hosts the answer-input portal, but paints nothing that could show through their glass.
   */
  isCovered?: boolean;
}) {
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const isSpaceBackgroundEnabled = useIsSpaceBackgroundEnabled();
  // The scene overlaps the primary navigation strip by this inset. The Learn summary lifts its
  // Start Reviewing row by the same amount, so the deck's row lands exactly where that one was.
  const primaryNavigationStripFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  // Other tabs can use the keyboard while this retained deck stays prepared at its full size.
  const isKeyboardVisible = usePersistentKeyboardVisible() && isActive;
  const bottomPadding = 9 + (isKeyboardVisible ? 0 : primaryNavigationStripFadeInset);
  const { reviewMode } = useActorUser();
  const [isAnswerBarHidden] = useDeviceSetting("isAnswerBarHidden");
  const [presentation, stage] = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.current.presentation,
    (c) => c.stage,
  );
  const { sessionId } = presentation;
  const presentationKey = learnCardPresentationKey(presentation);
  const [item, face, view, transition, flip] = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.current.item,
    (c) => c.state.face,
    (c) => c.state.view,
    (c) => c.state.transition,
    (c) => c.flip,
  );
  const isSentenceProduction =
    item.kind === "review" && isSentenceProductionDrillKind(item.drillType.kind);
  const isRevealed = view === "info" || face === "back";
  const hasAction = transition.phase === "committed" || transition.phase === "exiting";
  const hasContinueAction = hasAction && transition.action.kind === "continue";
  const hasRatings = item.kind === "review" && !isSentenceProduction && !hasContinueAction;
  const areRatingsActive = hasRatings && (isRevealed || hasAction);
  // Mount the ratings (including their interval query) before reveal. The same UI-thread
  // progress that exposes the back swaps the footer without waiting for the React drag event.
  const ratingsStyle = useAnimatedStyle(() => {
    const isVisible =
      hasRatings &&
      (isRevealed ||
        hasAction ||
        (stage.motionPresentation.get().current === presentationKey &&
          stage.flipProgress.get() > 0));
    return { display: isVisible ? "flex" : "none" };
  });
  const frontStyle = useAnimatedStyle(() => {
    const isHidden =
      hasRatings &&
      (isRevealed ||
        hasAction ||
        (stage.motionPresentation.get().current === presentationKey &&
          stage.flipProgress.get() > 0));
    return {
      opacity: isHidden || isRevealed || hasAction ? 0 : 1,
      pointerEvents: !isActive || isHidden || isRevealed || hasAction ? "none" : "box-none",
    };
  });
  // `null` while the slide has not registered its controls yet (its content can still be suspended).
  const [slideControls] = useContextSelectors(LearnDeckContext, (c) =>
    c.cardControls?.kind === "slide" ? c.cardControls : null,
  );
  const isMeaningFront = item.kind === "review" && item.drillType.kind === "meaning" && !isRevealed;
  const hasAnswerInput =
    item.kind === "review" &&
    (item.drillType.kind === "meaning" ||
      item.drillType.kind === "pronunciation" ||
      item.drillType.kind === "word-sentence") &&
    reviewMode === "answer" &&
    !isAnswerBarHidden;

  const answerInputHeight = isLandscape
    ? hasAnswerInput
      ? diameter
      : 0
    : diameter + bottomPadding;
  const answerInputBottomPadding = isLandscape ? 0 : bottomPadding;

  let content;
  if (item.kind === "slide") {
    if (slideControls === null) {
      // Awaiting the slide's controls: the plain continue button, labelled like a slide with no
      // custom `nextButtonLabel`, so the row doesn't jump when they arrive.
      content = <LearnFlashcardSlideControls label="Next Card" pages={null} />;
    } else {
      content = slideControls.hasCustomFooter ? null : (
        <LearnFlashcardSlideControls
          label={slideControls.nextButtonLabel}
          pages={slideControls.pages}
        />
      );
    }
  } else if (item.kind === "quiz") {
    content = <LearnFlashcardQuizControls />;
  } else if (isSentenceProduction) {
    content = hasAction ? (
      <LearnFlashcardActionButton
        action={{ kind: "continue" }}
        handlesAllActions
        label="Continue"
        testID="sentence-review-continue"
      />
    ) : null;
  } else if (hasContinueAction && item.kind === "review") {
    content = (
      <LearnFlashcardActionButton
        action={{ kind: "continue" }}
        handlesAllActions
        label="Continue"
        testID="review-continue"
      />
    );
  } else if (isRevealed || hasAction) {
    content = item.kind === "review" ? null : <LearnFlashcardLessonControls />;
  } else if (item.kind === "lesson") {
    content = <LearnFlashcardLessonControls isFront />;
  } else if (hasAnswerInput) {
    content = null;
  } else if (isLandscape) {
    content = (
      <LearnFlashcardActionButton
        action={{ kind: "continue" }}
        icon="LuEye"
        label="Reveal answer"
        onPress={() => flip("back")}
        testID="learn-card-reveal-button"
      />
    );
  } else {
    content = (
      <View className="size-full items-center justify-center" pointerEvents="box-none">
        {!isMeaningFront ? (
          // Drawn straight onto the strip, so it takes light ink over the space background.
          <SpaceSkyForegroundScope enabled={isSpaceBackgroundEnabled}>
            <Text
              className="text-center text-sm"
              colorVariant="muted"
              pointerEvents="none"
              testID="learn-card-reveal-hint"
            >
              Tap to reveal
            </Text>
          </SpaceSkyForegroundScope>
        ) : null}
      </View>
    );
  }

  return (
    // The same parent in both orientations, so the answer input's portal host never remounts.
    <LearnMapSheetCovered
      className={isLandscape ? "absolute inset-0" : "shrink-0"}
      style={isLandscape ? undefined : { height: answerInputHeight }}
    >
      {/* Keep the same portal parent across rotation, reveal, Info and undo. */}
      <Animated.View
        accessibilityElementsHidden={isCovered || !isActive || isRevealed || hasAction}
        className={cn(
          isLandscape ? "absolute bottom-4.5 px-2.25" : "shrink-0 px-2.25",
          isCovered && "opacity-0",
        )}
        importantForAccessibility={
          isCovered || !isActive || isRevealed || hasAction ? "no-hide-descendants" : "auto"
        }
        pointerEvents={isCovered ? "none" : "box-none"}
        style={[
          {
            height: answerInputHeight,
            left: isLandscape ? diameter + 18 : undefined,
            paddingBottom: answerInputBottomPadding,
            right: isLandscape ? diameter + 18 : undefined,
          },
          isLandscape ? frontStyle : undefined,
        ]}
      >
        <LearnMapCardsBackdrop />
        <SpaceSkySurfaceContext value>
          <Animated.View
            accessibilityElementsHidden={!isActive || isRevealed || hasAction}
            className="size-full"
            importantForAccessibility={
              !isActive || isRevealed || hasAction ? "no-hide-descendants" : "auto"
            }
            style={frontStyle}
            testID="learn-card-front-controls"
          >
            <PortalHost className="size-full" name={`learn-card-answer-input-${sessionId}`} />
          </Animated.View>
        </SpaceSkySurfaceContext>
      </Animated.View>
      <View
        accessibilityElementsHidden={isCovered}
        className={cn(
          isLandscape
            ? "absolute right-2.25 bottom-2.25 items-center"
            : "absolute inset-x-2.25 justify-center",
          isCovered && "opacity-0",
        )}
        importantForAccessibility={isCovered ? "no-hide-descendants" : "auto"}
        pointerEvents={isCovered ? "none" : "box-none"}
        style={
          isLandscape
            ? { top: diameter + 18, width: diameter }
            : { bottom: bottomPadding, height: diameter }
        }
        testID="learn-card-action-area"
      >
        <SpaceSkySurfaceContext value>
          {content === null ? null : isLandscape ? (
            <View className="size-full" pointerEvents="box-none">
              {content}
            </View>
          ) : (
            <View className="size-full justify-center" pointerEvents="box-none">
              <LearnMapCardsBackdrop />
              {content}
            </View>
          )}
          {hasRatings && item.kind === "review" ? (
            <Animated.View
              accessibilityElementsHidden={!areRatingsActive}
              className="absolute inset-0 z-10 justify-center"
              importantForAccessibility={areRatingsActive ? "auto" : "no-hide-descendants"}
              key={presentationKey}
              pointerEvents={areRatingsActive ? "auto" : "none"}
              style={ratingsStyle}
              testID="learn-card-rating-controls"
            >
              <LearnMapCardsBackdrop />
              {isLandscape ? (
                <View className="size-full">
                  <LearnFlashcardRatingButtons
                    drillType={item.drillType}
                    grapheme={item.grapheme}
                  />
                </View>
              ) : (
                <LearnFlashcardRatingButtons drillType={item.drillType} grapheme={item.grapheme} />
              )}
            </Animated.View>
          ) : null}
        </SpaceSkySurfaceContext>
      </View>
    </LearnMapSheetCovered>
  );
}
